import { useRef, memo, useCallback } from 'react'
import { Modal } from '@mui/material'
import { usePopupContext } from '../hooks/PopupContext'
import { PopupAndCardState } from '@/types/components/popup'
import EventBriteCustomForm from './EventBriteCustomForm'
import BizzaboCustomForm from './BizzaboCustomForm'
import useApiWrapper from '../services/api/apiWrapper'
import { useAuth } from '../hooks/useAuth'
import { useMixPanel } from '../hooks/useMixPanel'


interface CustomPopupContextType {
  popupAndCardState: PopupAndCardState
  setPopupAndCardState: React.Dispatch<React.SetStateAction<PopupAndCardState>>
  isApiCallInProgressRef: React.MutableRefObject<boolean>
}

/**
 * CustomPopupForm is a component that renders the custom popup form for the user.
 * @param isOpen - boolean to control the modal open state
 * @improvements - currently the dismiss popup supports only one form at a time.
 * We need to improve this to just dismiss the custom form that is currently open.
 * See adding an isVisible attribute to the externalFormPlatform array during clicking of
 * main CTA in the Announcement component.
 */
const CustomPopupForm: React.FC<{ isOpen: boolean }> = memo(({ isOpen }) => {
  const {
    popupAndCardState,
    setPopupAndCardState,
    isApiCallInProgressRef
  } = usePopupContext() as CustomPopupContextType
  const isFormSubmittedRef = useRef({})
  // @ts-expect-error untyped auth value
  const { userInfo, setAndStoreUserInfo } = useAuth()
  const api = useApiWrapper()
  const { logEvent } = useMixPanel()
  const qualifiedEvents = popupAndCardState.qualifiedEvents
  const popupData = popupAndCardState.popupData

  const handlePopupAttributeUpdate = useCallback(async (type: 'skip' | 'close' = 'skip') => {
    // Prevent multiple concurrent API calls
    if (isApiCallInProgressRef.current) return

    const activeCustomFormEvent = qualifiedEvents.find(item => item.isCustomFormPopupActive)
    const activeEventIndex = popupData.findIndex(item => item.contentId === activeCustomFormEvent.id)

    try {
      isApiCallInProgressRef.current = true

      const isCardForm = type === 'skip'
      const isClosed = type === 'close'

      setPopupAndCardState(prev => {
        const events = prev.popupData
        events[activeEventIndex].isClosed = isClosed
        events[activeEventIndex].isCardForm = isCardForm

        return { ...prev, popupData: events }
      })

      const payload = [{
        popupId: activeCustomFormEvent.id,
        isClosed,
        isCardForm
      }]

      await api.put(`${process.env.REACT_APP_OFFICE_BE_PH_URL}/v1/user/${userInfo?.unicityId}/dismiss-popup`,
        payload,
        {
          addPrefix: false,
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      )

      if (type === 'close') {
        isFormSubmittedRef.current[`${activeCustomFormEvent.id}-submitted`] = true
      }

      if (type === 'skip') {
        const platform = activeCustomFormEvent.platform
        logEvent(`skipped_${platform}_form`, {
          unicity_id: userInfo?.unicityId,
          country_code: userInfo?.countryCode,
          popup_content_id: activeCustomFormEvent.id,
        })
      }

      const popupAttributes = userInfo?.customAttributes?.popup || {}
      const updatedValues = {
        ...popupAttributes,
        [activeCustomFormEvent.id]: {
          isCardForm,
          isClosed
        }
      }

      setAndStoreUserInfo({
        ...userInfo,
        customAttributes: {
          ...userInfo?.customAttributes,
          popup: updatedValues,
        }
      })
    } catch (error) {
      console.error('[CustomPopupForm] Error updating popup attributes:', error)
    } finally {
      // Always reset the flag, even if an error occurs
      isApiCallInProgressRef.current = false
    }
  }, [popupData, userInfo, api, setAndStoreUserInfo, logEvent, qualifiedEvents, setPopupAndCardState, isApiCallInProgressRef])

  // Add safety check for externalFormPlatform
  if (Array.isArray(qualifiedEvents) && qualifiedEvents?.length === 0) {
    return null
  }

  const handleCustomFormModalClose = async () => {
    setPopupAndCardState(prev => ({
      ...prev,
      qualifiedEvents: prev.qualifiedEvents.map(item => ({ ...item, isCustomFormPopupActive: false }))
    }))
    if (Object.keys(isFormSubmittedRef.current).length === 0) {
      await handlePopupAttributeUpdate()
    } else {
      for (const key in isFormSubmittedRef.current) {
        if (!isFormSubmittedRef.current[key]) {
          await handlePopupAttributeUpdate()
        }
      }
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleCustomFormModalClose}
      aria-labelledby="popup-modal"
      aria-describedby="popup-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      }}
      data-test-id="eventbrite-modal"
    >
      <>
        {
          qualifiedEvents
            .map((item) => {
              const { id, platform, isCustomFormPopupActive } = item
              const data = popupData.find(popupItem => popupItem.contentId === id)
              // Add safety check for data
              if (!data || !isCustomFormPopupActive || !platform) {
                return null
              }

              switch (platform) {
                case 'eventbrite':
                  return <EventBriteCustomForm
                    ref={isFormSubmittedRef}
                    key={id}
                    data={data}
                    handlePopupAttributeUpdate={handlePopupAttributeUpdate}
                  />
                case 'bizzabo':
                  return <BizzaboCustomForm
                    ref={isFormSubmittedRef}
                    data={data}
                    key={id}
                    handlePopupAttributeUpdate={handlePopupAttributeUpdate}
                  />
                default:
                  return null
              }
            })
        }
      </>
    </Modal>
  )
})

CustomPopupForm.displayName = 'CustomPopupForm'

export default CustomPopupForm